import { RpcError, RpcRequestError } from 'viem'

/**
 * Classifies a raw RPC transport error into a low-cardinality `error_type`
 * bucket, so the `rpc_usage` wide event stays queryable without leaking raw
 * provider messages as labels.
 */
import {
  errorChain,
  isExecutionRevertError,
  isProxyUpstreamFailure,
  isTransportFailure
} from '@repo/web3/utils'

import {
  describeBotIdScriptFailure,
  isBotIdScriptLoadError
} from '@/lib/observability/client/botid-script-error'

function extractErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined

  if ('status' in error && typeof error.status === 'number') return error.status
  if ('statusCode' in error && typeof error.statusCode === 'number') return error.statusCode
  if (
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status
  }

  if ('cause' in error && error.cause !== error) {
    return extractErrorStatus(error.cause)
  }

  return undefined
}

/**
 * An HTTP status written as text: `Status: 503` (viem `HttpRequestError` message), `HTTP 503`
 * (the `/api/rpc` upstream suffix). Bare digits are not a status: viem messages embed the
 * request body, whose calldata and block numbers contain `429` or `50x` by chance.
 */
const HTTP_STATUS_TEXT_PATTERN = /\b(?:status(?: code)?|http):?\s*(\d{3})\b/i

const SERVER_ERROR_STATUSES: ReadonlySet<number> = new Set([500, 502, 503, 504])

function textHttpStatus(message: string): number | undefined {
  const match = HTTP_STATUS_TEXT_PATTERN.exec(message)
  return match ? Number(match[1]) : undefined
}

function isDomException(error: object): boolean {
  return typeof DOMException !== 'undefined' && error instanceof DOMException
}

/** JSON-RPC 2.0 reserved range for pre-defined and implementation-defined server errors. */
const JSON_RPC_RESERVED_CODE_MIN = -32768
const JSON_RPC_RESERVED_CODE_MAX = -32000
const VIEM_UNKNOWN_ERROR_CODE = -1

/**
 * The JSON-RPC code of the innermost error in the chain that carries one: the server's own answer.
 * viem's typed wrappers copy it from their `RpcRequestError` cause anyway. A DOMException's
 * legacy `code` is not a JSON-RPC code; viem's default `-1`, when no server answer is behind it,
 * means no code.
 */
function jsonRpcErrorCode(error: unknown): number | undefined {
  let code: number | undefined
  for (const node of errorChain(error)) {
    if (
      'code' in node &&
      typeof node.code === 'number' &&
      Number.isInteger(node.code) &&
      !isViemUnknownCode(node) &&
      !isDomException(node)
    ) {
      code = node.code
    }
  }
  return code
}

/** viem's `RpcError` defaults to -1 when no `RpcRequestError` (the server's answer) is behind it. */
function isViemUnknownCode(error: object): boolean {
  return (
    error instanceof RpcError &&
    error.code === VIEM_UNKNOWN_ERROR_CODE &&
    !(error.cause instanceof RpcRequestError)
  )
}

/** The server (or the proxy) answered: an HTTP status, a JSON-RPC error code, or the proxy's upstream-failure suffix. */
function hasServerResponse(error: unknown): boolean {
  return errorChain(error).some(
    node =>
      ('status' in node && typeof node.status === 'number') ||
      ('code' in node && typeof node.code === 'number' && !isDomException(node)) ||
      ('message' in node &&
        typeof node.message === 'string' &&
        isProxyUpstreamFailure(node.message))
  )
}

/**
 * Deterministic node rejections of initcode that exceeds the code-size limit
 * (`EVM error: CreateContractSizeLimit`, `max initcode size exceeded`), reported
 * by nodes as non-revert JSON-RPC failures (e.g. code -32003).
 */
const CODE_SIZE_LIMIT_PATTERN =
  /create(?:contract|initcode)sizelimit|max (?:init)?code size exceeded/i

function leafStrings(error: object): string[] {
  const candidates: unknown[] = [
    'message' in error ? error.message : undefined,
    'details' in error ? error.details : undefined,
    'shortMessage' in error ? error.shortMessage : undefined
  ]
  return candidates.filter((value): value is string => typeof value === 'string')
}

function isCodeSizeLimitLeaf(error: object): boolean {
  return leafStrings(error).some(text => CODE_SIZE_LIMIT_PATTERN.test(text))
}

/**
 * Whether `error` is a node-level rejection for exceeding the contract/initcode
 * size limit. Deterministic like a revert — the same request can never succeed —
 * but reported with a distinct code/message, so it gets its own bucket. The same
 * transport-failure short-circuit as `isExecutionRevertError` applies.
 */
export function isCodeSizeLimitError(error: unknown): boolean {
  const chain = errorChain(error)
  if (chain.some(isTransportFailure)) return false
  return chain.some(isCodeSizeLimitLeaf)
}

/** Deterministic EVM failures the caller cannot fix by retrying. */
export function isDeterministicEvmError(error: unknown): boolean {
  return isExecutionRevertError(error) || isCodeSizeLimitError(error)
}

export type RpcErrorType =
  | 'execution_reverted'
  | 'code_size_limit'
  | 'rate_limit'
  | 'timeout'
  | 'connection_error'
  | 'page_hidden'
  | 'offline'
  | 'botid_script_failed'
  | 'server_error'
  | 'invalid_response'
  | 'unknown'
  | ForbiddenRpcErrorType
  | `error_http_${number}`
  | `jsonrpc_${number}`
  | 'jsonrpc_other'

export interface RpcErrorContext {
  pageHidden?: boolean
  offline?: boolean
}

/**
 * Where a `/api/rpc` HTTP 403 was produced. `forbidden_rpc_proxy` means an upstream 403 passed
 * through our function. `forbidden_botid`, challenge, firewall and robots are intentional policy
 * blocks, not RPC reliability failures; `forbidden_non_vercel` came from an unknown non-Vercel
 * layer. Unattributable 403s stay `error_http_403`.
 */
export type ForbiddenRpcErrorType =
  | 'forbidden_vercel_challenge'
  | 'forbidden_vercel_firewall'
  | 'forbidden_robots'
  | 'forbidden_botid'
  | 'forbidden_non_vercel'
  | 'forbidden_rpc_proxy'

/** Sources with a separate signal or intentional edge/agent block need no per-call Sentry capture. */
export const BLOCKED_RPC_ERROR_TYPES: ReadonlySet<RpcErrorType> = new Set([
  'forbidden_vercel_challenge',
  'forbidden_vercel_firewall',
  'forbidden_robots',
  'forbidden_botid',
  'botid_script_failed'
])

/**
 * `x-vercel-id` is `<edge>::<function region>::<id>` when a function served the response, and
 * `<edge>::<id>` when the Vercel edge answered on its own (firewall deny, challenge).
 */
const VERCEL_EDGE_ONLY_ID_PATTERN = /^[a-z]{3}\d::[^:]+$/
const VERCEL_FIREWALL_DENY_BODY_PATTERN = /"code":"403".*"id":"[a-z]{3}\d::[^:"]+"/
const VERCEL_CHALLENGE_BODY_MARKER = 'Vercel Security Checkpoint'
const ROBOTS_DISALLOW_PATTERN = /\brobots(\.txt)? disallow/i

function responseHeaders(chain: object[]): Headers | undefined {
  for (const error of chain) {
    if ('headers' in error && error.headers instanceof Headers) return error.headers
  }
  return undefined
}

function errorDetails(chain: object[]): string {
  return chain
    .map(error => {
      if ('details' in error && typeof error.details === 'string') return error.details
      return error instanceof Error ? error.message : ''
    })
    .join('\n')
}

/**
 * Attributes an HTTP 403 from `/api/rpc` to the layer that produced it, from the response body
 * viem keeps in `details` and the response headers it keeps on `HttpRequestError`.
 */
export function classifyForbiddenRpcError(
  error: unknown
): ForbiddenRpcErrorType | 'error_http_403' {
  const chain = errorChain(error)
  const headers = responseHeaders(chain)
  const details = errorDetails(chain)
  if (
    (headers?.get('x-botid-verdict') === 'bot' && headers.get('x-botid-mode') !== 'observe') ||
    details.includes('Request blocked by bot protection')
  ) {
    return 'forbidden_botid'
  }

  const vercelId = headers?.get('x-vercel-id')
  if (vercelId && !VERCEL_EDGE_ONLY_ID_PATTERN.test(vercelId)) return 'forbidden_rpc_proxy'

  if (details.includes(VERCEL_CHALLENGE_BODY_MARKER)) return 'forbidden_vercel_challenge'
  if (VERCEL_FIREWALL_DENY_BODY_PATTERN.test(details)) return 'forbidden_vercel_firewall'
  if (ROBOTS_DISALLOW_PATTERN.test(details)) return 'forbidden_robots'

  if (!headers) return 'error_http_403'
  return vercelId ? 'forbidden_vercel_firewall' : 'forbidden_non_vercel'
}

function classifyRpcErrorBase(error: unknown): RpcErrorType {
  if (errorChain(error).some(isBotIdScriptLoadError)) return 'botid_script_failed'
  if (isCodeSizeLimitError(error)) return 'code_size_limit'
  if (isExecutionRevertError(error)) return 'execution_reverted'

  if (extractErrorStatus(error) === 403) return classifyForbiddenRpcError(error)

  const message = (error instanceof Error ? error.message : String(error)).toLowerCase()
  const textStatus = textHttpStatus(message)

  if (
    message.includes('rate limit') ||
    textStatus === 429 ||
    message.includes('too many requests') ||
    message.includes('request limit exceeded')
  ) {
    return 'rate_limit'
  }
  if (
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('etimedout')
  ) {
    return 'timeout'
  }
  if (
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('load failed') ||
    message.includes('connection') ||
    message.includes('econnrefused') ||
    message.includes('enotfound')
  ) {
    return 'connection_error'
  }

  const status = extractErrorStatus(error)
  if (typeof status === 'number' && status >= 400) {
    return `error_http_${status}`
  }

  if (
    (textStatus !== undefined && SERVER_ERROR_STATUSES.has(textStatus)) ||
    message.includes('bad gateway') ||
    message.includes('unavailable') ||
    message.includes('internal server error')
  ) {
    return 'server_error'
  }
  if (
    message.includes('invalid response') ||
    message.includes('parse error') ||
    message.includes('json')
  ) {
    return 'invalid_response'
  }

  const code = jsonRpcErrorCode(error)
  if (code === undefined) return 'unknown'
  if (code >= JSON_RPC_RESERVED_CODE_MIN && code <= JSON_RPC_RESERVED_CODE_MAX) {
    return `jsonrpc_${-code}`
  }
  return 'jsonrpc_other'
}

export function classifyRpcError(error: unknown, context: RpcErrorContext = {}): RpcErrorType {
  const errorType = classifyRpcErrorBase(error)
  if (errorType === 'botid_script_failed') {
    if (context.offline) return 'offline'
    if (context.pageHidden) return 'page_hidden'
  }
  if (hasServerResponse(error)) return errorType
  if (errorType !== 'connection_error' && errorType !== 'timeout' && errorType !== 'unknown') {
    return errorType
  }

  if (context.offline) return 'offline'
  if (context.pageHidden) return 'page_hidden'
  return errorType
}

const RPC_ERROR_MESSAGE_MAX_LENGTH = 300
const LONG_HEX_PATTERN = /0x[0-9a-f]{40,}/gi
const URL_PATTERN = /\b[a-z][a-z0-9+.-]*:\/\/\S+/gi

function firstLine(text: string): string {
  return text.split('\n', 1)[0]?.trim() ?? ''
}

/**
 * Short, log-safe description of an RPC failure: each error's viem
 * `shortMessage` (or first message line) and `details` along the cause chain,
 * without the request URL/body viem appends to `message`. Addresses, calldata
 * and URLs are masked so the text can ride on aggregated `rpc_usage` events.
 */
export function describeRpcError(error: unknown): string {
  const parts: string[] = []
  const add = (text: unknown) => {
    if (typeof text !== 'string') return
    const line = firstLine(text)
    if (line && !parts.includes(line)) parts.push(line)
  }

  const botIdFailure = errorChain(error).find(isBotIdScriptLoadError)
  if (botIdFailure) {
    const { script, status } = describeBotIdScriptFailure(botIdFailure)
    add(`BotID script ${script} failed to load (status ${status ?? 'unknown'})`)
  }

  for (const entry of errorChain(error)) {
    add('shortMessage' in entry ? entry.shortMessage : entry instanceof Error ? entry.message : '')
    if ('details' in entry) add(entry.details)
  }
  if (parts.length === 0) add(String(error))

  const description = parts
    .join(' | ')
    .replace(LONG_HEX_PATTERN, '0x…')
    .replace(URL_PATTERN, '<url>')
  return description.length > RPC_ERROR_MESSAGE_MAX_LENGTH
    ? `${description.slice(0, RPC_ERROR_MESSAGE_MAX_LENGTH - 1)}…`
    : description
}

export function isInterruptedRpcError(type: RpcErrorType | undefined): boolean {
  return type === 'page_hidden' || type === 'offline'
}
